import { getDomain, getEnvironment, isDev } from '@library/environment/isomorphic'
import { composeFetch, type RequestConfig } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  setHeader,
  setHost,
  setMethod,
} from '@library/http/requests'

import type {
  ApplyDashboardPromotionCodeRequest,
  ApplyDashboardPromotionCodeResponse,
  DashboardExperienceOrderDeliveryDetailsResponse,
  DashboardExperienceOrderResponse,
  DashboardExperienceOrderSkipDiscountResponse,
  DashboardExperienceResponse,
  UpdateDashboardExperienceOrderRequest,
} from './interfaces'

const LOCAL_API_HOST = 'https://staging-api.gousto.info'
const DAY_IN_MS = 24 * 60 * 60 * 1000
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/
const DELIVERY_TITLE_PATTERN =
  /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)(?:\s+\d{4})?$/i
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS_BY_NAME: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
}

function getActivateApiHost() {
  if (isDev()) {
    return LOCAL_API_HOST
  }

  return `https://${getEnvironment()}-api.${getDomain()}`
}

const normaliseAvailableDeliveryDays = (
  availableDeliveryDays: DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'],
): DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'] => {
  if (!availableDeliveryDays) {
    return availableDeliveryDays
  }

  return Object.fromEntries(
    Object.entries(availableDeliveryDays).map(([date, deliveryDay]) => {
      const {
        is_default: snakeCaseIsDefault,
        core_day_id: snakeCaseCoreDayId,
        unavailable_reason: snakeCaseUnavailableReason,
        alternate_delivery_day: snakeCaseAlternateDeliveryDay,
        slots,
        ...restDeliveryDay
      } = deliveryDay as typeof deliveryDay & {
        is_default?: boolean
        core_day_id?: string
        unavailable_reason?: string
        alternate_delivery_day?: string | null
      }

      return [
        date,
        {
          ...restDeliveryDay,
          isDefault: deliveryDay.isDefault ?? snakeCaseIsDefault ?? false,
          coreDayId: deliveryDay.coreDayId ?? snakeCaseCoreDayId ?? '',
          unavailableReason: deliveryDay.unavailableReason ?? snakeCaseUnavailableReason ?? '',
          alternateDeliveryDay:
            deliveryDay.alternateDeliveryDay ?? snakeCaseAlternateDeliveryDay ?? null,
          slots: slots.map((slot) => {
            const {
              day_slot_lead_time_id: snakeCaseDaySlotLeadTimeId,
              core_slot_id: snakeCaseCoreSlotId,
              delivery_start_time: snakeCaseDeliveryStartTime,
              delivery_end_time: snakeCaseDeliveryEndTime,
              delivery_price: snakeCaseDeliveryPrice,
              when_cutoff: snakeCaseWhenCutoff,
              default_day: snakeCaseDefaultDay,
              day_slot_lead_time_is_express: snakeCaseDaySlotLeadTimeIsExpress,
              is_default: snakeCaseSlotIsDefault,
              cutoff_day: snakeCaseCutoffDay,
              cutoff_time: snakeCaseCutoffTime,
              ...restSlot
            } = slot as typeof slot & {
              day_slot_lead_time_id?: string
              core_slot_id?: string
              delivery_start_time?: string
              delivery_end_time?: string
              delivery_price?: string
              when_cutoff?: string
              default_day?: number
              day_slot_lead_time_is_express?: boolean
              is_default?: boolean
              cutoff_day?: number
              cutoff_time?: string
            }

            return {
              ...restSlot,
              daySlotLeadTimeId: slot.daySlotLeadTimeId ?? snakeCaseDaySlotLeadTimeId ?? '',
              coreSlotId: slot.coreSlotId ?? snakeCaseCoreSlotId ?? '',
              deliveryStartTime: slot.deliveryStartTime ?? snakeCaseDeliveryStartTime ?? '',
              deliveryEndTime: slot.deliveryEndTime ?? snakeCaseDeliveryEndTime ?? '',
              deliveryPrice: slot.deliveryPrice ?? snakeCaseDeliveryPrice ?? '',
              whenCutoff: slot.whenCutoff ?? snakeCaseWhenCutoff ?? '',
              defaultDay: slot.defaultDay ?? snakeCaseDefaultDay ?? 0,
              daySlotLeadTimeIsExpress:
                slot.daySlotLeadTimeIsExpress ?? snakeCaseDaySlotLeadTimeIsExpress ?? false,
              isDefault: slot.isDefault ?? snakeCaseSlotIsDefault ?? false,
              cutoffDay: slot.cutoffDay ?? snakeCaseCutoffDay ?? 0,
              cutoffTime: slot.cutoffTime ?? snakeCaseCutoffTime ?? '',
            }
          }),
        },
      ]
    }),
  )
}

const getDateKey = (date: Date): string => {
  const year = date.getUTCFullYear().toString().padStart(4, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const day = date.getUTCDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

const parseIsoDate = (date: string): Date | null => {
  const match = date.match(ISO_DATE_PATTERN)

  if (!match) {
    return null
  }

  const [, year, month, day] = match
  const parsedDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  if (
    parsedDate.getUTCFullYear() !== Number(year) ||
    parsedDate.getUTCMonth() !== Number(month) - 1 ||
    parsedDate.getUTCDate() !== Number(day)
  ) {
    return null
  }

  return parsedDate
}

const getDeliveryDateFromTitle = (title: string, analyticsDeliveryDate: string): string | null => {
  const analyticsDate = parseIsoDate(analyticsDeliveryDate)
  const titleMatch = title.trim().match(DELIVERY_TITLE_PATTERN)

  if (!analyticsDate || !titleMatch) {
    return null
  }

  const [, weekdayName, day, monthName] = titleMatch
  const month = MONTHS_BY_NAME[monthName.toLowerCase()]

  if (!month) {
    return null
  }

  const analyticsYear = analyticsDate.getUTCFullYear()
  const candidateYears = [analyticsYear - 1, analyticsYear, analyticsYear + 1]

  for (const candidateYear of candidateYears) {
    const candidateDate = new Date(Date.UTC(candidateYear, month - 1, Number(day)))

    if (
      candidateDate.getUTCFullYear() !== candidateYear ||
      candidateDate.getUTCMonth() !== month - 1 ||
      candidateDate.getUTCDate() !== Number(day)
    ) {
      continue
    }

    const candidateWeekday = WEEKDAY_NAMES[candidateDate.getUTCDay()]

    if (candidateWeekday.toLowerCase() !== weekdayName.toLowerCase()) {
      continue
    }

    if (Math.abs(candidateDate.getTime() - analyticsDate.getTime()) <= DAY_IN_MS) {
      return getDateKey(candidateDate)
    }
  }

  return null
}

// The dashboard API can report the analytics date one UTC day behind the user-facing title.
const normaliseOrderAnalyticsDeliveryDate = (
  order: DashboardExperienceOrderResponse,
): DashboardExperienceOrderResponse => {
  const deliveryDateFromTitle = getDeliveryDateFromTitle(order.title, order.analytics.delivery_date)

  if (!deliveryDateFromTitle || deliveryDateFromTitle === order.analytics.delivery_date) {
    return order
  }

  return {
    ...order,
    analytics: {
      ...order.analytics,
      delivery_date: deliveryDateFromTitle,
    },
  }
}

const normaliseDashboardExperience = (
  dashboardExperience: DashboardExperienceResponse,
): DashboardExperienceResponse => {
  if (!dashboardExperience.orders) {
    return dashboardExperience
  }

  return {
    ...dashboardExperience,
    orders: dashboardExperience.orders.map(normaliseOrderAnalyticsDeliveryDate),
  }
}

const parseDashboardExperience = async (
  response: Response,
): Promise<DashboardExperienceResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const dashboardExperience = (await response.json()) as DashboardExperienceResponse

  return normaliseDashboardExperience(dashboardExperience)
}

const parseOrderDeliveryDetails = async (
  response: Response,
): Promise<DashboardExperienceOrderDeliveryDetailsResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const orderDeliveryDetails =
    (await response.json()) as DashboardExperienceOrderDeliveryDetailsResponse

  return {
    ...orderDeliveryDetails,
    available_delivery_days: normaliseAvailableDeliveryDays(
      orderDeliveryDetails.available_delivery_days,
    ),
  }
}

const parseUpdateDashboardExperienceOrder = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()

    if (
      response.status === 500 &&
      (responseMessage.trim() === '' || responseMessage.trim() === 'Internal Server Error')
    ) {
      return null
    }

    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

const parseSkipOrderResponse = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

const isOrderSkipDiscountResponse = (
  responseBody: unknown,
): responseBody is DashboardExperienceOrderSkipDiscountResponse => {
  if (typeof responseBody !== 'object' || responseBody === null || Array.isArray(responseBody)) {
    return false
  }

  const skipDiscount = responseBody as Record<string, unknown>

  return (
    typeof skipDiscount.title === 'string' &&
    typeof skipDiscount.subtitle === 'string' &&
    typeof skipDiscount.discount === 'string' &&
    typeof skipDiscount.applyCta === 'string' &&
    typeof skipDiscount.skipCta === 'string' &&
    typeof skipDiscount.isExistingDiscount === 'boolean' &&
    (typeof skipDiscount.promoCode === 'string' || skipDiscount.promoCode === null)
  )
}

const parseOrderSkipDiscountResponse = async (
  response: Response,
): Promise<DashboardExperienceOrderSkipDiscountResponse | null> => {
  if (response.status === 204) {
    return null
  }

  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  try {
    const responseBody = (await response.json()) as unknown

    return isOrderSkipDiscountResponse(responseBody) ? responseBody : null
  } catch {
    return null
  }
}

const isApplyDashboardPromotionCodeResponse = (
  responseBody: unknown,
): responseBody is ApplyDashboardPromotionCodeResponse => {
  if (typeof responseBody !== 'object' || responseBody === null || Array.isArray(responseBody)) {
    return false
  }

  const applyPromotionCodeResponse = responseBody as Record<string, unknown>

  return (
    typeof applyPromotionCodeResponse.promoCode === 'string' &&
    (applyPromotionCodeResponse.status === 'applied' ||
      applyPromotionCodeResponse.status === 'alreadyApplied')
  )
}

const parseApplyDashboardPromotionCodeResponse = async (
  response: Response,
): Promise<ApplyDashboardPromotionCodeResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const responseBody = (await response.json()) as unknown

  if (!isApplyDashboardPromotionCodeResponse(responseBody)) {
    throw new Error('service responded with invalid apply promotion code response')
  }

  return responseBody
}

const parseRestoreOrderResponse = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

export const fetchDashboardExperience = composeFetch(
  composeRequest(setHost(getActivateApiHost), addPath('activate/dashboard/v1'), goustoHeaders),
  parseDashboardExperience,
)

export const fetchOrderDeliveryDetails = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/order_delivery_details/v1'),
    addPath((orderId) => orderId),
    goustoHeaders,
  ),
  parseOrderDeliveryDetails,
)

export const updateDashboardExperienceOrder = composeFetch(
  composeRequest<{ orderId: string; body: UpdateDashboardExperienceOrderRequest }>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/update-order/v1'),
    addPath(({ orderId }) => orderId),
    setHeader('Content-Type', 'application/json'),
    setMethod('PUT'),
    (
      request: RequestConfig,
      input: { orderId: string; body: UpdateDashboardExperienceOrderRequest },
    ) => {
      const requestBody = JSON.stringify(input.body)

      request.body = requestBody

      if (isDev()) {
        // Surface the exact request payload in local development when the backend returns opaque 500s.
        // eslint-disable-next-line no-console
        console.info('updateDashboardExperienceOrder request body', JSON.parse(requestBody))
      }

      return request
    },
    goustoHeaders,
  ),
  parseUpdateDashboardExperienceOrder,
)

export const skipOrder = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/skip_order/v1'),
    addPath((orderId) => orderId),
    setMethod('DELETE'),
    goustoHeaders,
  ),
  parseSkipOrderResponse,
)

export const fetchOrderSkipDiscount = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/order_skip_discount/v1'),
    addPath((orderId) => orderId),
    goustoHeaders,
  ),
  parseOrderSkipDiscountResponse,
)

export const applyDashboardPromotionCode = composeFetch(
  composeRequest<ApplyDashboardPromotionCodeRequest>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/apply_promotion_code/v1'),
    setHeader('Content-Type', 'application/json'),
    setMethod('POST'),
    (request: RequestConfig, input: ApplyDashboardPromotionCodeRequest) => {
      request.body = JSON.stringify(input)

      return request
    },
    goustoHeaders,
  ),
  parseApplyDashboardPromotionCodeResponse,
)

export const restoreOrder = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/restore_order/v1'),
    addPath((orderId) => orderId),
    setMethod('POST'),
    goustoHeaders,
  ),
  parseRestoreOrderResponse,
)
